import { Divider } from "~/ui/Divider"
import { VideoPreviewModule } from "~/av/VideoPreviewModule"
import { useTestFlow } from "./TestFlowProvider"
import invariant from "tiny-invariant"
import { QuestionTypeEnum } from "~/__generated__/graphql"
import { TextResponseForm } from "./TextResponseForm"
import { VideoResponseForm } from "./VideoResponseForm"
import { SingleChoiceResponseForm } from "./SingleChoiceResponseForm"
import { MultipleChoiceResponseForm } from "./MultipleChoiceResponseForm"
import { DocumentUploadResponseForm } from "./DocumentUploadResponseForm"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useEffect, useRef } from "react"
import { Timer, TimerHandles, useSecondsRemaining } from "./Timer"
import { AlertTriangle } from "lucide-react"
import { Button } from "~/shadcn/ui/button"

const QUESTION_TYPE_COMPONENTS: Record<
  QuestionTypeEnum,
  React.FC<{ timeExpired: boolean; onStopTimer: () => void }>
> = {
  [QuestionTypeEnum.TextResponse]: TextResponseForm,
  [QuestionTypeEnum.SingleChoice]: SingleChoiceResponseForm,
  [QuestionTypeEnum.MultipleChoice]: MultipleChoiceResponseForm,
  [QuestionTypeEnum.VideoResponse]: VideoResponseForm,
  [QuestionTypeEnum.DocumentUpload]: DocumentUploadResponseForm,
}

export const TestFlowQuestionForm = () => {
  const {
    currentAttempt,
    candidateTest,
    showNavigationWarning,
    hasSeenNavigationWarning,
    setHasSeenNavigationWarning,
  } = useTestFlow()
  invariant(candidateTest, "candidateTest expected")

  const [questionAttemptCheckIn] = useSafeMutation(
    QUESTION_ATTEMPT_CHECK_IN_MUTATION
  )
  const questionType = currentAttempt?.question.questionGroup.questionType
  const QuestionTypeComponent = questionType
    ? QUESTION_TYPE_COMPONENTS[questionType]
    : null

  const timerRef = useRef<TimerHandles>(null)
  const secondsRemaining = useSecondsRemaining({ currentAttempt })
  const timeExpired = secondsRemaining === 0

  const stopTimer = () => {
    timerRef.current?.stop()
  }

  useEffect(() => {
    if (!currentAttempt) return

    const intervalId = setInterval(() => {
      questionAttemptCheckIn({
        variables: { input: { questionAttemptId: currentAttempt.id } },
      })
    }, 1000)

    return () => clearInterval(intervalId)
  }, [currentAttempt, questionAttemptCheckIn])

  return (
    <div className="container mx-auto pb-12">
      <div className="py-3 text-gray-999 flex items-center">
        <div className="flex-1">{candidateTest.test.name}</div>
      </div>
      {showNavigationWarning && !hasSeenNavigationWarning ? (
        <div className="flex items-start w-full">
          <Divider className="mb-4" />
          <div className="w-full flex items-center justify-between px-3 py-2 mb-8 rounded text-sm border border-yellow-dark bg-yellow-light text-gray-2d2d2d">
            <div className="flex items-center">
              <AlertTriangle
                size={16}
                className="inline mr-3 text-yellow-dark"
              />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold">
                  Please remain on the test.
                </span>
                <span className="text-xs">
                  Using other applications during the test is discouraged and
                  will be recorded.
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end">
              <Button
                variant="link"
                size="sm"
                onClick={() => setHasSeenNavigationWarning(true)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Divider className="mb-16" />
      )}

      <div className="flex items-start">
        <div className="flex-1">
          {timeExpired && (
            <div className="flex flex-col gap-2 bg-communist text-white rounded p-4 text-center mb-8 text-sm">
              <p>Your time has expired for this question.</p>
              <p className="font-semibold">
                Click continue to go to the next question.
              </p>
            </div>
          )}

          <div className="bg-gray-F7F7F5 border border-gray-E6E6E3 rounded p-6 mb-6">
            <div className="mb-6 text-gray-999 flex items-center">
              <div className="flex-1">
                Question {candidateTest.currentQuestionNumber} of{" "}
                {candidateTest.questionCount}
              </div>

              {secondsRemaining !== null && (
                <Timer ref={timerRef} secondsRemaining={secondsRemaining} />
              )}
            </div>
            <div className="font-weight font-semibold text-2xl text-gray-333">
              {currentAttempt?.question.questionCopy}
            </div>
          </div>

          {QuestionTypeComponent && (
            <QuestionTypeComponent
              timeExpired={timeExpired}
              onStopTimer={stopTimer}
            />
          )}
        </div>

        <VideoPreviewModule />
      </div>
    </div>
  )
}

const QUESTION_ATTEMPT_CHECK_IN_MUTATION = gql(`
  mutation QuestionAttemptCheckIn($input: QuestionAttemptCheckInInput!) {
    questionAttemptCheckIn(input: $input) {
      candidateTest {
        ...TestFlow_CandidateTest
      }
    }
  }
`)
